import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Bind from 'lodash-decorators/bind';
import Debounce from 'lodash-decorators/debounce';
import noop from 'lodash/noop';

import { isOnMobile } from 'utils/device-utils';

import styles from './Tabs.module.scss';

import { ReactComponent as ArrowLeft } from 'common/images/arrow_left.svg';

import TabsNav from './TabsNav/TabsNav';

export class Tabs extends Component {
  state = {
    activeSlideIndex: 0,
    sliderRef: null,
    navRef: null,
    isMobile: isOnMobile()
  };

  static propTypes = {
    className: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.array, PropTypes.element])
      .isRequired,
    transition: PropTypes.bool,
    padding: PropTypes.string,
    showTabsNav: PropTypes.bool,
    showArrowOnMobile: PropTypes.bool,
    showArrowOnDesktop: PropTypes.bool,
    prevArrowClass: PropTypes.string,
    nextArrowClass: PropTypes.string,
    onSlideChange: PropTypes.func
  };

  static defaultProps = {
    className: '',
    transition: false,
    padding: '0',
    arrowColor: '#321e75',
    showTabsNav: true,
    showArrowOnMobile: false,
    showArrowOnDesktop: true,
    prevArrowClass: '',
    nextArrowClass: '',
    onSlideChange: noop
  };

  constructor(props) {
    super(props);

    this.items = this.getItems();
  }

  componentDidMount() {
    this.updateSliderHeight();
    this.onSlideChange();
    window.addEventListener('resize', this.updateDimensions);
  }

  componentDidUpdate() {
    this.updateSliderHeight();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  render() {
    const withTransition = this.props.transition;
    const { activeSlideIndex, isMobile } = this.state;
    const {
      className,
      arrowColor,
      showTabsNav,
      showArrowOnMobile,
      showArrowOnDesktop,
      prevArrowClass,
      nextArrowClass
    } = this.props;

    const items = this.getItems();
    const slidesLength = items.length;

    const showPrevArrow = activeSlideIndex > 0;
    const showNextArrow = activeSlideIndex < slidesLength - 1;

    const slidesStyles = {
      width: `${100 * slidesLength}%`,
      transform: `translateX(${-1 * activeSlideIndex * (100 / slidesLength)}%)`
    };

    const slideStyles = {
      padding: `${this.props.padding}`
    };

    const arrowStyle = {
      fill: arrowColor
    };

    const Arrows = () => (
      <div>
        {showPrevArrow && (
          <span
            className={`${styles.Arrow} ${styles.ArrowPrev} ${prevArrowClass}`}
            onClick={this.setPrevSlide}
          >
            <ArrowLeft style={arrowStyle} />
          </span>
        )}

        {showNextArrow && (
          <span
            className={`${styles.Arrow} ${styles.ArrowNext} ${nextArrowClass}`}
            onClick={this.setNextSlide}
          >
            <ArrowLeft style={arrowStyle} />
          </span>
        )}
      </div>
    );

    const shouldDisplayArrows =
      (!isMobile && showArrowOnDesktop) || (isMobile && showArrowOnMobile);
    const shouldDisplayTabsNav = showTabsNav && items.length > 1;

    return (
      <div className={className}>
        {shouldDisplayTabsNav && (
          <TabsNav
            ref={ref => (this.navRef = ref)}
            items={items}
            onChangeSlide={this.setActiveSlide}
            activeSlideIndex={this.state.activeSlideIndex}
          />
        )}

        <div className={styles.SliderWrapper}>
          <div className={styles.Slider}>
            <div
              style={slidesStyles}
              className={`${styles.SlidesWrapper} ${
                withTransition ? styles.Transition : ''
              }`}
              ref="slidesWrapper"
            >
              {items.map((slide, index) => (
                <div
                  key={index}
                  className={styles.Slide}
                  style={slideStyles}
                  ref={`slide${index}`}
                >
                  {slide.props.children}
                </div>
              ))}
            </div>
          </div>

          {shouldDisplayArrows && <Arrows />}
        </div>
      </div>
    );
  }

  @Bind()
  @Debounce(200)
  updateDimensions() {
    this.updateSliderHeight();
    this.setState({
      isMobile: isOnMobile()
    });
  }

  updateSliderHeight() {
    const activeSlide = this.refs[`slide${this.state.activeSlideIndex}`];
    if (activeSlide) {
      const activeSlideHeight = activeSlide.clientHeight;

      this.refs.slidesWrapper.style.height = `${activeSlideHeight}px`;
    }
  }

  getItems() {
    const items = this.props.children.filter(item => item.type === Panel);

    if (!items) {
      throw new Error('Tabs must contain at least one Panel');
    }

    return Array.isArray(items) ? items : [items];
  }

  @Bind()
  setActiveSlide(index) {
    if (this.canChangeSlide(index)) {
      this.setState({ activeSlideIndex: index }, () => this.onSlideChange());
    }
  }

  onSlideChange() {
    const { activeSlideIndex } = this.state;
    this.props.onSlideChange({
      activeSlideIndex: activeSlideIndex,
      isFirstSlide: activeSlideIndex === 0,
      isLastSlide: this.items.length === activeSlideIndex + 1
    });
  }

  @Bind()
  setNextSlide() {
    this.setActiveSlide(this.state.activeSlideIndex + 1);
  }

  @Bind()
  setPrevSlide() {
    this.setActiveSlide(this.state.activeSlideIndex - 1);
  }

  canChangeSlide(index) {
    return index >= 0 && index < this.items.length;
  }
}

export class Panel extends Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    children: PropTypes.oneOfType([PropTypes.array, PropTypes.element])
      .isRequired
  };

  render() {
    return this.props.children;
  }
}



// WEBPACK FOOTER //
// ./src/common/components/Tabs/Tabs.js